import * as yup from "yup";
import { difference, includes } from "lodash";
import {
  FormQuestionData,
  EligibilityFormContext_submission_form_layouts_groups as GroupSectionProps,
} from "types/gql-types";
import logger from "services/logger";
import { defaultValidator, typeValidators } from "./components/fields";
import { keyFromIdentity } from "./components/helpers";
import { QuestionProps } from "./components/question";

import { Handle } from "./type";

export const passes = ({ cond }: Handle<any>, value: any): boolean => {
  if (cond === null) return true;

  const { type, values } = cond;
  if (type === "includes") {
    return (Array.isArray(value) && difference(values, value).length === 0) ||
    includes(values, value) || (includes(values, JSON.stringify(value)));
  }
  return false;
};

export const extendWithValidator = (
  validators: Record<string, unknown>,
  question: FormQuestionData,
  questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
// eslint-disable-next-line max-params
): Record<string, unknown> => {
  const getQuestionValidator = typeValidators[question.__typename] || defaultValidator;
  const questionValidator = getQuestionValidator(question, questionsMap, index && parent, index);
  if (parent) {
    return Object.entries(questionValidator)
      .reduce((acc, [key, val]) => ({
        ...acc,
        [key]: yup.mixed().when(parent, (parentVal: any, schema: any) =>
          (passes(question, parentVal) ? val : schema)),
      }), validators);
  }
  return {
    ...validators,
    ...questionValidator,
  };
};

const getQuestion = (name:string, questionMap:Record<string, QuestionProps>):QuestionProps => {
  const question = questionMap[name];

  if (!question) {
    logger.sendMessage(`Question ${name} not found`);

    return {} as QuestionProps;
  }

  return question;
};

export const extendWithValidatorNested = (
  validators: Record<string, unknown>,
  questionIdentity: string[],
  questionsMap: Record<string, QuestionProps>,
  parent?: string,
  index?: number,
// eslint-disable-next-line max-params
): Record<string, unknown> => {
  const questionName = keyFromIdentity(questionIdentity);
  const question = getQuestion(questionName, questionsMap);
  const { children } = question;
  return children.reduce(
    (acc: any, childIdentity: string[]) =>
      extendWithValidatorNested(acc, childIdentity, questionsMap, questionName, index),
    extendWithValidator(validators, question, questionsMap, parent, index),
  );
};

export const generateSectionValidations = (
  section: GroupSectionProps,
  questionsMap: Record<string, QuestionProps>,
): Record<string, unknown> => {
  const { questions: questionIdentities } = section;
  const sectionValidators = questionIdentities
    .reduce((acc: Record<string, unknown>, identity: string[]) =>
      extendWithValidatorNested(acc, identity, questionsMap), {});
  return sectionValidators;
};
